import { motion } from "framer-motion";
import { MonitorX, PackageOpen, Presentation, Hourglass, MessageSquareOff, Ghost } from "lucide-react";

const PAINS = [
  {
    icon: MonitorX,
    title: 'Sold a "software", handed an Excel file',
    body: "The ad shows a slick tool with a full walkthrough video. You pay, you download — and it's a spreadsheet. No software. Ever.",
    quote: '"Everything in the ad was the cover page. The product was not."',
    span: "md:col-span-4",
  },
  {
    icon: PackageOpen,
    title: '"108 editable documents" = free files, repackaged',
    body: "Promised BOQ samples, checklists, schedules. It's whatever they scraped free online, zipped and sold back for ₹299.",
    quote: '"They compile what\'s free online and sell it again."',
    span: "md:col-span-2",
  },
  {
    icon: Presentation,
    title: "The ₹9 webinar that never ends",
    body: 'Pay ₹9, sit three hours, hear what you already knew. "For the detailed version, register for the next class." Then the next. Then ₹3,000.',
    quote: '"They only teach the basic one, then push the next paid step."',
    span: "md:col-span-2",
  },
  {
    icon: Hourglass,
    title: '"Only 100 seats — offer ends tonight"',
    body: "Fake scarcity, fake countdowns. Designed to make you pay before you think. The offer returns tomorrow, for the next batch.",
    quote: '"One-day validity, hundred seats — pure pressure."',
    span: "md:col-span-4",
  },
  {
    icon: MessageSquareOff,
    title: "You were kept from other buyers on purpose",
    body: "Admin-only posts. Comments off. Muted chats. Hidden Zoom galleries. So no one warns you until after you've paid.",
    quote: '"They hide the group so people can\'t talk to each other."',
    span: "md:col-span-3",
  },
  {
    icon: Ghost,
    title: "No refund, no reply, no one to ask",
    body: "The money leaves, the seller vanishes. Often no way to even find who sold it. You eat the loss and stay quiet.",
    quote: '"I\'ve been fooled once or twice. Paid, tried, got rubbish."',
    span: "md:col-span-3",
  },
];

export const PainGrid = () => (
  <section data-testid="pain-section" className="py-24 sm:py-32">
    <div className="max-w-6xl mx-auto px-6">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6 }}
        className="max-w-2xl"
      >
        <div className="flex items-center gap-4">
          <span className="font-display text-4xl sm:text-5xl font-bold outline-word leading-none select-none" aria-hidden="true">01</span>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-warn">Sound familiar?</p>
        </div>
        <h2 className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
          The ad was the best part of the product.
        </h2>
        <p className="mt-4 text-base sm:text-lg text-gray-600">
          Every one of these has happened to real buyers — maybe to you. Nobody warned them, because nobody could.
        </p>
      </motion.div>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-6 gap-6">
        {PAINS.map((p, i) => (
          <motion.article
            key={p.title}
            data-testid={`pain-card-${i}`}
            className={`${p.span} group rounded-2xl bg-white border border-black/5 shadow-[0_10px_36px_-14px_rgba(17,24,39,0.12)] p-8 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_-16px_rgba(224,83,59,0.22)]`}
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.55, delay: (i % 3) * 0.1 }}
          >
            <span className="inline-flex w-11 h-11 items-center justify-center rounded-xl bg-warn-soft text-warn">
              <p.icon size={20} />
            </span>
            <h3 className="mt-5 font-display text-xl font-semibold tracking-tight leading-snug">{p.title}</h3>
            <p className="mt-3 text-sm text-gray-600 leading-relaxed">{p.body}</p>
            <p className="mt-5 border-l-2 border-gold pl-3 text-xs italic text-gray-500">{p.quote}</p>
          </motion.article>
        ))}
      </div>
    </div>
  </section>
);
